import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { dataManager } from '../lib/dataManager';
import { ImageViewer } from '../components/ui/ImageViewer';
import { PdfViewer } from '../components/ui/PdfViewer';
import { Image as ImageIcon, FileText } from 'lucide-react';
import { isPdfFile, generatePdfThumbnail, dataURLtoBlob } from '../lib/pdfUtils';
import { supabase } from '../lib/supabase';
import { CachedImage } from '../components/ui/CachedImage';
import { theme } from '../lib/theme';
import { useTranslations } from '../i18n';

interface ExpenseImage {
  id: string;
  image_url: string;
  image_url_thumb?: string;
  comment: string;
  created_at: string;
  log_entry_id: string;
  type: 'expense';
}

interface SparePartImage {
  id: string;
  image_url: string;
  image_url_thumb?: string;
  comment: string;
  created_at: string;
  spare_part_id: string;
  type: 'part';
}

type GalleryImage = ExpenseImage | SparePartImage;

interface GalleryProps {
  carId: string;
}

export function Gallery({ carId }: GalleryProps) {
  const [images, setImages] = useState<GalleryImage[]>(() => dataManager.getCachedGallery(carId) || []);
  const [loading, setLoading] = useState(() => !dataManager.getCachedGallery(carId));
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [pdfViewerProps, setPdfViewerProps] = useState<{ url: string; link?: { url: string; label: string } } | null>(null);
  const { t, language } = useTranslations();
  const { slug } = useParams();

  useEffect(() => {
    if (images.length > 0 && !images[0].type) {
      loadImages(true);
    } else {
      loadImages();
    }
  }, [carId]);

  const loadImages = async (force = false) => {
    try {
      const allImages = await dataManager.fetchGallery(carId, force);
      setImages(allImages);
    } catch (error) {
      console.error('Error loading gallery images:', error);
    } finally {
      setLoading(false);
    }
  };

  const processingThumbs = useRef(new Set());

  useEffect(() => {
    if (images.length > 0) {
      handleLazyThumbnails();
    }
  }, [images.length]);

  const handleLazyThumbnails = async () => {
    const pdfsToThumb = images.filter(img => {
      const isPdf = isPdfFile(img.image_url);
      const thumbIsActuallyPdf = img.image_url_thumb && isPdfFile(img.image_url_thumb);
      const needsThumb = isPdf && (!img.image_url_thumb || thumbIsActuallyPdf);
      const alreadyProcessing = processingThumbs.current.has(img.id);
      return needsThumb && !alreadyProcessing;
    });

    if (pdfsToThumb.length === 0) return;

    console.log(`🔍 [Gallery] LazyThumb checking ${pdfsToThumb.length} PDFs`);

    for (const img of pdfsToThumb) {
      if (processingThumbs.current.has(img.id)) continue;
      processingThumbs.current.add(img.id);

      try {
        console.log(`🖼️ [Gallery] Generating thumb for: ${img.image_url}`);
        const thumbDataUrl = await generatePdfThumbnail(img.image_url);
        const thumbBlob = dataURLtoBlob(thumbDataUrl);

        const emailWorkerUrl = 'my-car-email-processor.gtreulands.workers.dev';
        if (!img.image_url.includes(emailWorkerUrl)) continue;

        const fileMarker = '/file/';
        const markerIndex = img.image_url.indexOf(fileMarker);
        if (markerIndex === -1) continue;

        const storagePath = decodeURIComponent(img.image_url.substring(markerIndex + fileMarker.length));
        const finalWorkerBase = img.image_url.substring(0, markerIndex);
        console.log(`🚀 [Gallery] Uploading thumb for ${storagePath} to ${finalWorkerBase}`);

        const formData = new FormData();
        formData.append('storagePath', storagePath);
        formData.append('thumb', thumbBlob, 'thumb.jpg');

        const uploadRes = await fetch(`${finalWorkerBase}/upload-thumb`, {
          method: 'POST',
          body: formData
        });

        if (!uploadRes.ok) throw new Error(`Upload failed: ${uploadRes.status}`);

        const { thumbUrl } = await uploadRes.json();
        console.log(`🖼️ [Gallery] Uploaded to: ${thumbUrl}`);

        const tableMap = {
          expense: 'log_entry_images',
          part: 'spare_part_images'
        };
        const table = tableMap[img.type];

        const { error: updateErr } = await supabase
          .from(table)
          .update({ image_url_thumb: thumbUrl })
          .eq('id', img.id);

        if (updateErr) throw updateErr;

        setImages(prev => prev.map(p => p.id === img.id ? { ...p, image_url_thumb: thumbUrl } : p));
        console.log(`✅ [Gallery] Success for ${img.id}`);

      } catch (err) {
        console.error('❌ [Gallery] LazyThumb failed:', err);
      } finally {
        processingThumbs.current.delete(img.id);
      }
    }
  };

  const groupImagesByMonth = () => {
    const groups: Record<string, GalleryImage[]> = {};

    images.forEach((image) => {
      const date = new Date(image.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      if (!groups[monthKey]) {
        groups[monthKey] = [];
      }
      groups[monthKey].push(image);
    });

    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  };

  const formatMonthYear = (monthKey: string) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString(language, {
      year: 'numeric',
      month: 'long',
    });
  };

  const handleImageClick = (image: GalleryImage) => {
    let matchUrl = '';
    let label = '';
    if (slug) {
      if (image.type === 'expense') {
        matchUrl = `/${slug}/expenses/${(image as any).log_entry_id}`;
        label = (t.expenses as any).openExpense || 'Open Expense';
      } else if ((image.type as any) === 'note') {
        matchUrl = `/${slug}/notes/${(image as any).note_id}`;
        label = (t.notes as any).openNote || 'Open Note';
      } else {
        matchUrl = `/${slug}/parts/${(image as any).spare_part_id}`;
        label = (t.spareParts as any).openPart || 'Open Part';
      }
    }

    const relatedLink = slug ? {
      url: matchUrl,
      label: label
    } : undefined;

    if (isPdfFile(image.image_url)) {
      setPdfViewerProps({ url: image.image_url, link: relatedLink });
    } else {
      const imageOnlyList = images.filter(img => !isPdfFile(img.image_url));
      const index = imageOnlyList.findIndex(img => img.id === image.id);
      setViewerIndex(index);
      setViewerOpen(true);
    }
  };

  if (loading) {
    return (
      <div className={`${theme.spacing.pagePadding} pb-32 pt-4 relative`}>
        <div className={`${theme.colors.cardBg} rounded-[2rem] shadow-xl dark:shadow-none p-12 text-center border ${theme.colors.border}`}>
          <div className="inline-flex p-4 bg-blue-100 dark:bg-blue-900/20 rounded-full mb-4">
            <ImageIcon size={32} className="text-blue-600 dark:text-blue-400 animate-pulse" />
          </div>
          <p className="text-gray-600 dark:text-white text-lg font-semibold">{t.common.loading}...</p>
        </div>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className={`${theme.spacing.pagePadding} pb-32 pt-4 relative`}>
        <div className={`${theme.colors.cardBg} rounded-[2rem] shadow-xl dark:shadow-none p-12 text-center border ${theme.colors.border}`}>
          <div className="inline-flex p-4 bg-gray-100 dark:bg-white/5 rounded-full mb-4">
            <ImageIcon size={32} className="text-gray-400 dark:text-gray-600" />
          </div>
          <p className="text-gray-500 dark:text-gray-300 text-lg">{t.gallery.noImages}</p>
          <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">
            {t.gallery.addImagesNote}
          </p>
        </div>
      </div>
    );
  };

  const groupedImages = groupImagesByMonth();

  return (
    <>
      <div className={`${theme.spacing.pagePadding} pb-32 pt-4 relative`}>
        <div className="space-y-4">


          <div className="p-6 space-y-8">
            {groupedImages.map(([monthKey, monthImages]) => (
              <div key={monthKey}>
                <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-baseline gap-2">
                  {formatMonthYear(monthKey)}
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">({monthImages.length})</span>
                </h4>

                <div className="flex flex-wrap gap-4">
                  {monthImages.map((image, index) => {
                    const isPdf = isPdfFile(image.image_url);
                    // Use thumbnail if available, otherwise fallback to original
                    const displayUrl = image.image_url_thumb || image.image_url;

                    return (
                      <div key={image.id} className="w-[100px]">
                        <button
                          onClick={() => handleImageClick(image)}
                          className="w-[100px] h-[100px] rounded-lg overflow-hidden hover:opacity-90 transition-opacity relative group shrink-0"
                        >
                          {displayUrl ? (
                            <CachedImage
                              src={displayUrl}
                              alt={t.gallery.imageAlt.replace('{number}', (index + 1).toString())}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-white/5">
                              <FileText size={48} className="text-gray-400 dark:text-gray-600" />
                            </div>
                          )}

                          {(image.type === 'expense' || image.type === 'part') && image.comment && (
                            <>
                              <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />
                              <p className="absolute bottom-1.5 inset-x-0 text-[10px] leading-[1.1] text-white font-semibold w-full text-center px-1.5 line-clamp-2 drop-shadow-md pointer-events-none">
                                {image.comment}
                              </p>
                            </>
                          )}

                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                          {isPdf && (
                            <div className="absolute top-1 right-1 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded font-bold shadow">
                              PDF
                            </div>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>

      {viewerOpen && (
        <ImageViewer
          images={images.filter(img => !isPdfFile(img.image_url)).map(img => img.image_url)}
          initialIndex={viewerIndex}
          onClose={() => setViewerOpen(false)}
          relatedLinks={images.filter(img => !isPdfFile(img.image_url)).map(img => {
            if (!slug || !img.type) return undefined;
            let matchUrl = '';
            let label = '';
            if (img.type === 'expense') {
              matchUrl = `/${slug}/expenses/${(img as any).log_entry_id}`;
              label = (t.expenses as any).openExpense || 'Open Expense';
            } else if ((img.type as any) === 'note') {
              matchUrl = `/${slug}/notes/${(img as any).note_id}`;
              label = (t.notes as any).openNote || 'Open Note';
            } else {
              matchUrl = `/${slug}/parts/${(img as any).spare_part_id}`;
              label = (t.spareParts as any).openPart || 'Open Part';
            }
            return { url: matchUrl, label };
          })}
        />
      )}

      {pdfViewerProps && (
        <PdfViewer
          url={pdfViewerProps.url}
          onClose={() => setPdfViewerProps(null)}
          relatedLink={pdfViewerProps.link}
        />
      )}
    </>
  );
}
